import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Trophy,
  Users,
  Swords,
  ShoppingCart,
  Dumbbell,
  Shield,
  Zap,
  Target,
  Crown,
  CheckCircle,
  Globe,
  Monitor,
  Smartphone,
} from 'lucide-react';
import Footer from '../components/Footer';
import { Header } from '../components/Header';
import Seo from '../components/Seo';
import { faqs } from '../data/faqs';

const ease = [0.16, 1, 0.3, 1];

const jsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Can I play a football manager game online for free?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. My Football Game is a free browser-based football manager. No download, no subscription. Create your club at myfootballgame.com and start playing in under two minutes.' }
    },
    {
      '@type': 'Question',
      name: 'Do I need to download anything to play?',
      acceptedAnswer: { '@type': 'Answer', text: 'No. My Football Game runs entirely in your browser — desktop, tablet or mobile. No app store, no installer, no waiting.' }
    },
    {
      '@type': 'Question',
      name: 'How long does it take to start playing?',
      acceptedAnswer: { '@type': 'Answer', text: 'Under two minutes. Register, get your squad, set a formation, and send your first challenge. The whole process is instant.' }
    },
    {
      '@type': 'Question',
      name: 'Can I play with my friends?',
      acceptedAnswer: { '@type': 'Answer', text: 'Yes. Add friends, send direct challenges, create private leagues, and share match results on WhatsApp. My Football Game is built for social competition.' }
    },
    {
      '@type': 'Question',
      name: 'Is the game really free or is there a paywall?',
      acceptedAnswer: { '@type': 'Answer', text: 'Everything is free. Two currencies exist — coins (earned through matches) and gold (earned from wins). Optional gold coin purchases are available but every feature, player and league is accessible without paying.' }
    },
  ]
};

const PlayLanding = () => {
  return (
    <div className="min-h-[100dvh]" style={{ background: 'var(--surface-0)', color: 'var(--text-1)' }}>
      <Seo
        title="Play Football Manager Online Free | My Football Game"
        description="Play a free football manager online right now. Pick your eleven, set tactics and watch the match live, minute by minute. No download, works on any phone or PC."
        path="/play"
        jsonLd={jsonLd}
      />

      <div className="container mx-auto px-4 pt-8 pb-16">
        <Header />

        {/* ── HERO ── */}
        <div className="max-w-4xl mx-auto text-center pt-8 pb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease }}
          >
            <div className="pc-eyebrow pc-eyebrow-accent mb-3">100% Free · No Download · Play Now</div>
          </motion.div>

          <motion.h1
            className="pc-display text-5xl md:text-7xl lg:text-8xl mb-6 leading-[0.85]"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.6, ease }}
          >
            PLAY <span style={{ color: 'var(--accent)' }}>FOOTBALL MANAGER</span><br />ONLINE FREE
          </motion.h1>

          <motion.p
            className="text-base md:text-lg mb-8 max-w-2xl mx-auto"
            style={{ color: 'var(--text-2)' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Build your dream squad, set tactics, sign players on the transfer market and challenge other
            managers in live simulated matches. No download, no install — play right now in your browser.
          </motion.p>

          <motion.div
            className="flex flex-col sm:flex-row gap-3 justify-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.5 }}
          >
            <Link to="/register">
              <motion.button
                className="px-8 py-4 rounded font-bold text-[13px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 w-full sm:w-auto transition-all"
                style={{
                  background: 'var(--accent)',
                  color: 'var(--surface-0)',
                  fontFamily: 'var(--font-body)',
                  boxShadow: '0 0 32px var(--accent-glow)',
                }}
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
              >
                Create Free Club <ArrowRight className="h-4 w-4" />
              </motion.button>
            </Link>
            <Link to="/login">
              <motion.button
                className="px-8 py-4 rounded font-bold text-[13px] uppercase tracking-[0.2em] flex items-center justify-center gap-2 w-full sm:w-auto transition-all"
                style={{
                  background: 'transparent',
                  border: '1px solid var(--border-edge)',
                  color: 'var(--text-1)',
                  fontFamily: 'var(--font-body)',
                }}
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
              >
                Login
              </motion.button>
            </Link>
          </motion.div>

          {/* Trust signals */}
          <motion.div
            className="flex flex-wrap justify-center gap-6 mt-10 text-xs"
            style={{ color: 'var(--text-3)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            {[
              { icon: <CheckCircle className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />, text: 'Free forever' },
              { icon: <Monitor className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />, text: 'No download' },
              { icon: <Globe className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />, text: 'Play worldwide' },
              { icon: <Smartphone className="w-3.5 h-3.5" style={{ color: 'var(--accent)' }} />, text: 'Works on mobile' },
            ].map((item, i) => (
              <span key={i} className="flex items-center gap-1.5 font-medium">
                {item.icon} {item.text}
              </span>
            ))}
          </motion.div>
        </div>
      </div>

      {/* ── WHAT YOU GET ── */}
      <section className="py-20" style={{ background: 'var(--surface-1)' }}>
        <div className="container mx-auto px-4">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Everything a football manager game should have</h2>
            <p className="text-[var(--text-2)] max-w-2xl mx-auto">
              Deep strategy, live matches, a real transfer market and multiplayer competition — all free, all in your browser.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 max-w-5xl mx-auto">
            {[
              { icon: <Users className="w-6 h-6" />, title: 'Squad management', desc: '18 formations, 26 positions, physique system. Build your perfect starting eleven and manage squad depth.' },
              { icon: <Target className="w-6 h-6" />, title: 'Tactical control', desc: 'Formation, mentality, pressing style, set-piece takers. Every decision feeds into the match engine.' },
              { icon: <Swords className="w-6 h-6" />, title: 'Live match simulation', desc: 'Minute-by-minute events: goals, cards, injuries, substitutions. Watch your tactics play out in real time.' },
              { icon: <ShoppingCart className="w-6 h-6" />, title: 'Transfer market', desc: 'Buy and sell players with other managers. Scout bargains, flip for profit, build a dynasty.' },
              { icon: <Dumbbell className="w-6 h-6" />, title: 'Player training', desc: 'Develop individual attributes over time. Young talents grow faster, veterans bring consistency.' },
              { icon: <Trophy className="w-6 h-6" />, title: 'Leagues & tournaments', desc: '5 league tiers, private leagues with friends, weekly tournaments with gold prizes.' },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="rounded-xl p-6"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border-edge)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.06 }}
              >
                <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4" style={{ background: 'rgba(184,255,61,0.1)', color: 'var(--accent)' }}>
                  {item.icon}
                </div>
                <h3 className="font-bold mb-2">{item.title}</h3>
                <p className="text-sm text-[var(--text-3)] leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 3 STEPS ── */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Start playing in 3 steps</h2>
            <p className="text-[var(--text-2)]">From sign-up to your first match in under 2 minutes. No credit card, no app download.</p>
          </motion.div>

          <div className="space-y-8">
            {[
              { n: '1', title: 'Create your club', desc: 'Pick a name, get a full squad of unique players with real attributes, positions and nationalities. Your transfer budget is ready.' },
              { n: '2', title: 'Set tactics and lineup', desc: 'Choose from 18 formations. Assign players to their best positions. Set mentality, pressing and set-piece takers.' },
              { n: '3', title: 'Challenge and compete', desc: 'Send challenges to random opponents or friends. Watch matches live, earn coins, climb the league ladder and get promoted.' },
            ].map((step, i) => (
              <motion.div
                key={i}
                className="flex gap-5 items-start"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center pc-display text-2xl" style={{ background: 'var(--accent)', color: 'var(--surface-0)' }}>
                  {step.n}
                </div>
                <div>
                  <h3 className="font-bold text-lg mb-1">{step.title}</h3>
                  <p className="text-sm text-[var(--text-2)] leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <Link to="/register">
              <motion.button
                className="px-8 py-4 rounded-lg font-bold text-[13px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
                style={{ background: 'var(--accent)', color: 'var(--surface-0)', boxShadow: '0 0 24px var(--accent-glow)' }}
                whileHover={{ y: -2 }}
                whileTap={{ y: 0 }}
              >
                Play Now — It's Free <ArrowRight className="h-4 w-4" />
              </motion.button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── COMPARISON ── */}
      <section className="py-20" style={{ background: 'var(--surface-1)' }}>
        <div className="container mx-auto px-4 max-w-4xl">
          <motion.div
            className="text-center mb-14"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Why managers choose My Football Game</h2>
          </motion.div>

          <motion.div
            className="overflow-x-auto rounded-xl"
            style={{ border: '1px solid var(--border-edge)' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: 'var(--surface-2)' }}>
                  <th className="text-left p-4 font-bold">Feature</th>
                  <th className="p-4 font-bold text-center" style={{ color: 'var(--accent)' }}>My Football Game</th>
                  <th className="p-4 font-bold text-center text-[var(--text-3)]">Typical FM games</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Price', 'Free forever', '$30–50 + DLC'],
                  ['Download required', 'No — browser only', 'Yes — multi-GB'],
                  ['Mobile support', 'Full browser on any device', 'Separate paid app'],
                  ['Live match simulation', 'Real-time SSE streaming', 'Text-only or skip'],
                  ['Multiplayer', 'Friends + random worldwide', 'Limited or none'],
                  ['Private leagues', 'Unlimited, free', 'Not available'],
                  ['Transfer market', 'Player-driven economy', 'AI-only market'],
                  ['Time to first match', 'Under 2 minutes', '30+ minutes setup'],
                ].map(([feature, us, them], i) => (
                  <tr key={i} style={{ borderTop: '1px solid var(--border-edge)' }}>
                    <td className="p-4 font-medium">{feature}</td>
                    <td className="p-4 text-center font-bold" style={{ color: 'var(--accent)' }}>{us}</td>
                    <td className="p-4 text-center text-[var(--text-3)]">{them}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </motion.div>
        </div>
      </section>

      {/* ── PLATFORMS ── */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Play on any device</h2>
            <p className="text-[var(--text-2)]">Desktop, tablet, phone — if it has a browser, you can manage your club.</p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: <Monitor className="w-8 h-8" />, label: 'Desktop', desc: 'Chrome, Firefox, Safari, Edge — full experience on any screen.' },
              { icon: <Smartphone className="w-8 h-8" />, label: 'Mobile', desc: 'Manage your squad on the go. No app needed, just open your browser.' },
              { icon: <Globe className="w-8 h-8" />, label: 'Worldwide', desc: 'Play against managers from every country. No region locks.' },
            ].map((item, i) => (
              <motion.div
                key={i}
                className="rounded-xl p-6 text-center"
                style={{ background: 'var(--surface-1)', border: '1px solid var(--border-edge)' }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
              >
                <div className="mx-auto w-14 h-14 rounded-xl flex items-center justify-center mb-4" style={{ background: 'rgba(184,255,61,0.1)', color: 'var(--accent)' }}>
                  {item.icon}
                </div>
                <h3 className="font-bold mb-2">{item.label}</h3>
                <p className="text-sm text-[var(--text-3)]">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="py-20" style={{ background: 'var(--surface-1)' }}>
        <div className="container mx-auto px-4 max-w-3xl">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Common questions</h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.slice(0, 6).map((faq, i) => (
              <motion.details
                key={i}
                className="rounded-xl overflow-hidden"
                style={{ background: 'var(--surface-2)', border: '1px solid var(--border-edge)' }}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
              >
                <summary className="p-5 cursor-pointer font-bold text-sm hover:text-[var(--accent)] transition-colors">
                  {faq.q}
                </summary>
                <div className="px-5 pb-5 text-sm text-[var(--text-2)] leading-relaxed">
                  {faq.a}
                </div>
              </motion.details>
            ))}
          </div>

          <div className="text-center mt-6">
            <Link to="/faq" className="text-sm font-bold hover:underline" style={{ color: 'var(--accent)' }}>
              See all FAQs →
            </Link>
          </div>
        </div>
      </section>

      {/* ── SEO CONTENT ── */}
      <section className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          <h2 className="text-2xl md:text-3xl font-bold mb-6 text-center">Play football manager games online — no download needed</h2>
          <div className="space-y-4 text-sm md:text-base leading-relaxed" style={{ color: 'var(--text-2)' }}>
            <p>
              If you've ever searched for "play football manager online", "free football game" or "soccer
              manager game", you know how hard it is to find a good one. Most football management games
              require expensive downloads, paid subscriptions, or haven't been updated in years. My Football
              Game is different — it's a modern, free, browser-based football manager that you can play
              instantly on any device.
            </p>
            <p>
              As a football manager at myfootballgame.com, you take control of every aspect of your club.
              Build your squad by signing players from the transfer market — a real player-driven economy
              where you buy from and sell to other managers. Set your formation from 18 options, assign
              players to 26 detailed positions, and fine-tune your tactics with mentality, pressing and
              set-piece assignments. Then challenge other managers to live simulated matches where every
              minute counts.
            </p>
            <p>
              What sets My Football Game apart from other online football manager games is the live match
              simulation. Matches aren't just a score on a screen — they play out minute by minute with
              goals, assists, cards, injuries, substitutions and momentum shifts. Your tactical decisions
              directly shape every event. A well-set formation can beat a squad with higher ratings, and
              smart set-piece assignments win tight matches on fine margins.
            </p>
            <p>
              The social features make My Football Game the best football manager game to play with friends.
              Send direct challenges, create private leagues with invite codes, and share your match results
              on WhatsApp. Private leagues run full seasons with standings, promotion, relegation and gold
              coin prizes — bringing the intensity of real league football to your friend group.
            </p>
            <p>
              My Football Game works on every platform: desktop browsers (Chrome, Firefox, Safari, Edge),
              tablets and mobile phones. There's no app to download, no storage space required, and no
              difference between the mobile and desktop experience. Open myfootballgame.com in any browser
              and you're playing within two minutes.
            </p>
            <p>
              Ready to play?{' '}
              <Link to="/register" className="font-bold" style={{ color: 'var(--accent)' }}>Create your free club now</Link>{' '}
              and join thousands of managers competing worldwide. It's free, it's instant, and your first
              match is just a challenge away.
            </p>
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="py-20" style={{ background: 'var(--surface-1)' }}>
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-3xl mx-auto rounded-2xl p-8 md:p-16 text-center relative overflow-hidden"
            style={{ background: 'var(--surface-0)', border: '1px solid var(--border-edge)' }}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="absolute inset-0 opacity-5" style={{ background: 'radial-gradient(circle at 50% 0%, var(--accent), transparent 60%)' }} />
            <h2 className="text-3xl md:text-4xl font-bold mb-4 relative z-10">
              Your club is waiting
            </h2>
            <p className="text-lg text-[var(--text-2)] mb-8 relative z-10">
              Free to play. No download. No credit card. Create your club and play your first match right now.
            </p>
            <div className="relative z-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/register">
                <motion.button
                  className="px-8 py-4 rounded-lg font-bold text-[13px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
                  style={{ background: 'var(--accent)', color: 'var(--surface-0)', boxShadow: '0 0 32px var(--accent-glow)' }}
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  Start Playing Free <ArrowRight className="h-4 w-4" />
                </motion.button>
              </Link>
              <Link to="/how-to-play">
                <motion.button
                  className="px-8 py-4 rounded-lg font-bold text-[13px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
                  style={{ background: 'transparent', border: '1px solid var(--border-edge)', color: 'var(--text-1)' }}
                  whileHover={{ y: -2 }}
                  whileTap={{ y: 0 }}
                >
                  How to Play
                </motion.button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PlayLanding;
